import { db } from "../firebase";
import {
  collection, getDocs,
  addDoc, deleteDoc, doc, serverTimestamp, updateDoc
} from "firebase/firestore";

const CLIENTI_BEF = ["cdp", "consorziostandby"];

function isClienteBEF(nomeCliente) {
  const nome = (nomeCliente || "").toLowerCase();
  return CLIENTI_BEF.some((c) => nome.includes(c));
}

function giorniDifferenza(data1, data2) {
  const d1 = new Date(data1);
  const d2 = new Date(data2);
  return Math.floor((d2 - d1) / (1000 * 60 * 60 * 24));
}

function dataEventoRiferimento(evento) {
  if (evento.durataGiorni > 1 && evento.giornate?.length > 0) {
    return evento.giornate[0]?.dataInizio;
  }
  return evento.dataInizio || evento.data;
}

function dataFineRiferimento(evento) {
  if (evento.durataGiorni > 1 && evento.giornate?.length > 0) {
    const last = evento.giornate[evento.giornate.length - 1];
    return last?.dataFine || last?.dataInizio;
  }
  return evento.dataFine || evento.dataInizio || evento.data;
}

export async function aggiornaNotifiche(clientiMap) {
  const oggi = new Date().toISOString().split("T")[0];

  const eventiSnap = await getDocs(collection(db, "eventi"));
  const eventi = eventiSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const notificheSnap = await getDocs(collection(db, "notifiche"));
  const notificheEsistenti = notificheSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const notificheMap = {};
  const notificheIgnorate = new Set();

  notificheEsistenti.forEach((n) => {
    const key = `${n.tipo}_${n.eventoId}`;
    notificheMap[key] = n;
    if (n.ignorata) notificheIgnorate.add(key);
  });

  const notificheAttive = new Set();

  for (const evento of eventi) {
    const nomeCliente = clientiMap[evento.clienteId] || "";
    const titolo = evento.titolo || "Evento senza titolo";

    // 1. EVENTO IMMINENTE
    if (["chiusoAcquisito", "preventivo"].includes(evento.stato)) {
      const dataEvento = dataEventoRiferimento(evento);
      if (dataEvento) {
        const giorniMancanti = giorniDifferenza(oggi, dataEvento);
        if (giorniMancanti >= 0 && giorniMancanti <= 5) {
          const key = `evento_imminente_${evento.id}`;
          notificheAttive.add(key);
          const messaggio = `"${titolo}" è tra ${giorniMancanti} giorn${giorniMancanti === 1 ? "o" : "i"}`;
          if (!notificheMap[key] && !notificheIgnorate.has(key)) {
            await addDoc(collection(db, "notifiche"), {
              tipo: "evento_imminente",
              eventoId: evento.id,
              titolo: "Evento imminente",
              messaggio,
              gravita: "warning",
              letta: false,
              ignorata: false,
              createdAt: serverTimestamp(),
            });
          } else if (notificheMap[key] && !notificheIgnorate.has(key)) {
            await updateDoc(doc(db, "notifiche", notificheMap[key].id), { messaggio });
          }
        }
      }
    }

    // 2. BEF NON RICEVUTO
    if (evento.stato === "completato" && isClienteBEF(nomeCliente) && !evento.befRicevuto) {
      const dataFine = dataFineRiferimento(evento);
      if (dataFine) {
        const giorniPassati = giorniDifferenza(dataFine, oggi);
        if (giorniPassati >= 15) {
          const key = `bef_non_ricevuto_${evento.id}`;
          notificheAttive.add(key);
          const messaggio = `"${titolo}" — BEF in attesa da ${giorniPassati} giorni`;
          if (!notificheMap[key] && !notificheIgnorate.has(key)) {
            await addDoc(collection(db, "notifiche"), {
              tipo: "bef_non_ricevuto",
              eventoId: evento.id,
              titolo: "BEF non ricevuto",
              messaggio,
              gravita: "error",
              letta: false,
              ignorata: false,
              createdAt: serverTimestamp(),
            });
          } else if (notificheMap[key] && !notificheIgnorate.has(key)) {
            await updateDoc(doc(db, "notifiche", notificheMap[key].id), { messaggio });
          }
        }
      }
    }

    // 3. TASK SUITECRM NON CREATO
    if (evento.stato === "completato" && !isClienteBEF(nomeCliente) && !evento.taskSuiteCRMCreato) {
      const dataFine = dataFineRiferimento(evento);
      if (dataFine) {
        const giorniPassati = giorniDifferenza(dataFine, oggi);
        if (giorniPassati >= 3) {
          const key = `task_suitecrm_${evento.id}`;
          notificheAttive.add(key);
          const messaggio = `"${titolo}" — task di fatturazione non creato dopo ${giorniPassati} giorni`;
          if (!notificheMap[key] && !notificheIgnorate.has(key)) {
            await addDoc(collection(db, "notifiche"), {
              tipo: "task_suitecrm",
              eventoId: evento.id,
              titolo: "Task SuiteCRM mancante",
              messaggio,
              gravita: "error",
              letta: false,
              ignorata: false,
              createdAt: serverTimestamp(),
            });
          } else if (notificheMap[key] && !notificheIgnorate.has(key)) {
            await updateDoc(doc(db, "notifiche", notificheMap[key].id), { messaggio });
          }
        }
      }
    }

    // 4. EVENTO RIMANDATO SENZA NUOVA DATA
    if (evento.stato === "rimandato" && !evento.nuovaData) {
      const dataAggiornamento = evento.updatedAt?.toDate?.()?.toISOString()?.split("T")[0];
      if (dataAggiornamento) {
        const giorniPassati = giorniDifferenza(dataAggiornamento, oggi);
        if (giorniPassati >= 15) {
          const key = `rimandato_senza_data_${evento.id}`;
          notificheAttive.add(key);
          const messaggio = `"${titolo}" è rimandato da ${giorniPassati} giorni senza nuova data`;
          if (!notificheMap[key] && !notificheIgnorate.has(key)) {
            await addDoc(collection(db, "notifiche"), {
              tipo: "rimandato_senza_data",
              eventoId: evento.id,
              titolo: "Evento rimandato senza data",
              messaggio,
              gravita: "warning",
              letta: false,
              ignorata: false,
              createdAt: serverTimestamp(),
            });
          } else if (notificheMap[key] && !notificheIgnorate.has(key)) {
            await updateDoc(doc(db, "notifiche", notificheMap[key].id), { messaggio });
          }
        }
      }
    }
  }

  // Elimina fisicamente solo le notifiche non più attive e non ignorate
  for (const n of notificheEsistenti) {
    const key = `${n.tipo}_${n.eventoId}`;
    if (!notificheAttive.has(key) && !n.ignorata) {
      await deleteDoc(doc(db, "notifiche", n.id));
    }
  }

  return notificheAttive.size;
}